/** Вкладка «Поставки» (`tab=supplies`, ручка `/supplies`): путь поставки «Приёмка → attr19
 *  (подсказка) → Оценка», метод и уверенность, места сейчас, сверка и флаги. Фильтры — в адресе
 *  (state.ts): нет параметра — умолчание /meta.supply_defaults, `-` — фильтр снят. Поставка —
 *  одна строка (поля поставки бэкенд берёт из одной строки витрины, не суммирует). */
import { PanelRight } from "lucide-react";
import React from "react";

import { WB_ALL, type SupplyStatusFilter, type WbMeta, type WbSupplies } from "@/api/wbRegions";
import { cn } from "@/lib/utils";
import { fmtWhen } from "../ozon-analytics/format";
import { selectClass, thClass } from "../ozon-analytics/ui";
import { ErrorBox, Loading } from "./cells";
import type { useWbSupplies } from "./queries";
import { OFF, RESET_SUPPLY_FILTERS, type Patch, type SupplyCardRef, type SupplyParams, type SupplyQueryResult } from "./state";
import { useDebouncedText } from "./useDebouncedText";
import { FlagsList, FlowCell, MethodBadge, PathChips, PlacesList, PlannedMark, ReconCell } from "./SupplyBits";
import { methodLabel, statusFilterLabel, storageLabel } from "./supplyFormat";

/** Поиск номера с задержкой: в адрес (`sq`) — через 400 мс после ввода. */
function NumberSearch({ value, onChange }: { value: string; onChange: (v: string) => void }): React.ReactElement {
  const [draft, setDraft] = useDebouncedText(value, onChange);
  return (
    <input
      type="search"
      inputMode="numeric"
      aria-label="Поиск по номеру поставки"
      placeholder="№ поставки"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      className="h-8 w-40 rounded-md border bg-background px-2 text-xs"
    />
  );
}

function Filters({
  meta,
  params,
  status,
  since,
  onPatch,
}: {
  meta: WbMeta;
  params: SupplyParams;
  /** действующие статусы (адрес или умолчание); null — фильтр снят */
  status: SupplyStatusFilter[] | null;
  since: string | null;
  onPatch: (p: Patch) => void;
}): React.ReactElement {
  const defaults = meta.supply_defaults;
  const toggle = (st: SupplyStatusFilter): void => {
    const cur = status ?? [];
    const next = cur.includes(st) ? cur.filter((x) => x !== st) : [...cur, st];
    // сняли последний — фильтр снят (все статусы), а не «ничего»
    onPatch({ st: next.length ? meta.status_filters.filter((x) => next.includes(x)).join(",") : OFF });
  };
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs" data-testid="supply-filters">
      <fieldset className="flex flex-wrap items-center gap-2">
        <legend className="sr-only">Статус хранения</legend>
        <span className="text-muted-foreground" title="Отмеченные — через «или»: «запланирована» — ещё не принята (статус WB)">
          Статус:
        </span>
        {meta.status_filters.map((st) => (
          <label key={st} className="flex items-center gap-1">
            <input type="checkbox" checked={status?.includes(st) ?? false} onChange={() => toggle(st)} />
            {statusFilterLabel(st)}
          </label>
        ))}
        {status === null && <span className="text-muted-foreground" data-testid="status-off">(все статусы)</span>}
      </fieldset>
      <label className="flex items-center gap-1.5">
        с даты
        <input
          type="date"
          aria-label="Поставки с даты"
          value={since ?? ""}
          onChange={(e) => onPatch({ since: e.target.value || OFF })}
          className="h-8 rounded-md border bg-background px-2 text-xs"
        />
      </label>
      {since !== null && (
        <button type="button" className="-ml-2 text-muted-foreground hover:underline" onClick={() => onPatch({ since: OFF })}>
          без даты
        </button>
      )}
      <label className="flex items-center gap-1.5">
        <input
          type="checkbox"
          checked={params.flagsOnly}
          onChange={(e) => onPatch({ flags: e.target.checked ? "1" : null })}
        />
        Только с флагами
      </label>
      <label className="flex items-center gap-1.5">
        Метод
        <select
          aria-label="Метод оценки склада"
          className={selectClass}
          value={params.method ?? ""}
          onChange={(e) => onPatch({ method: e.target.value || null })}
        >
          <option value="">все</option>
          {meta.supply_methods.map((m) => (
            <option key={m} value={m}>
              {methodLabel(m).short}
            </option>
          ))}
        </select>
      </label>
      <NumberSearch value={params.sq} onChange={(v) => onPatch({ sq: v || null })} />
      {(params.st !== undefined || params.since !== undefined) && (
        <button
          type="button"
          className="text-muted-foreground hover:underline"
          title={`Умолчание: ${defaults.status.map(statusFilterLabel).join(", ")}; с ${fmtWhen(defaults.since)}`}
          onClick={() => onPatch({ st: null, since: null })}
        >
          вернуть умолчания
        </button>
      )}
    </div>
  );
}

function SuppliesTable({
  data,
  showLegal,
  onOpenCard,
}: {
  data: WbSupplies;
  showLegal: boolean;
  onOpenCard: (c: SupplyCardRef) => void;
}): React.ReactElement {
  return (
    <div className="max-h-[70vh] overflow-auto rounded-md border">
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            {showLegal && <th className={cn(thClass, "text-left")}>Кабинет</th>}
            <th className={cn(thClass, "text-left")}>№</th>
            <th className={cn(thClass, "text-left")}>Дата · статус WB</th>
            <th className={cn(thClass, "text-left")} title="attr19 — подсказка кабинета, не склад поставки">
              Путь: приёмка → attr19 → оценка
            </th>
            <th className={cn(thClass, "text-left")}>Метод · уверенность</th>
            <th className={cn(thClass, "text-left")}>Сейчас: где, шт.</th>
            <th className={thClass} title="Принято · продано FBO · возвраты на склад WB · вывоз продавцу (нетто)">
              Принято · продано · возвр. · вывоз
            </th>
            <th className={thClass} title="принято − хранение − продано + возвраты − вывоз; флаг > 10 %">
              Расхождение шт. · %
            </th>
            <th className={cn(thClass, "text-left")}>Флаги</th>
          </tr>
        </thead>
        <tbody>
          {data.supplies.map((s) => (
            <tr key={`${s.legal_name}:${s.supply_id}`} className="border-t align-top" data-testid="supply-row">
              {showLegal && <td className="px-2 py-1 text-muted-foreground">{s.legal_name}</td>}
              <td className="px-2 py-1">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-medium tabular-nums hover:underline"
                  title="Открыть карточку поставки"
                  onClick={() => onOpenCard({ kind: "supply", legal: s.legal_name, supplyId: s.supply_id })}
                >
                  {s.supply_id}
                  <PanelRight className="size-3 text-muted-foreground" aria-hidden />
                </button>
              </td>
              <td className="px-2 py-1">
                <div className="whitespace-nowrap">
                  {s.supply_date ? fmtWhen(s.supply_date) : <span className="text-muted-foreground">без даты</span>}
                </div>
                <div className="text-muted-foreground">{s.status_name ?? "нет статуса WB"}</div>
                <div className="text-[10px] text-muted-foreground" data-testid="storage-status">
                  {storageLabel(s.storage_status)} <PlannedMark s={s} />
                </div>
              </td>
              <td className="px-2 py-1">
                <PathChips s={s} />
              </td>
              <td className="px-2 py-1">
                <MethodBadge s={s} />
              </td>
              <td className="px-2 py-1">
                <PlacesList s={s} />
              </td>
              <td className="px-2 py-1 text-right">
                <FlowCell s={s} />
              </td>
              <td className="px-2 py-1 text-right">
                <ReconCell s={s} />
              </td>
              <td className="px-2 py-1">
                <FlagsList s={s} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SuppliesTab({
  meta,
  legal,
  params,
  q,
  supplies,
  onPatch,
  onOpenCard,
}: {
  meta: WbMeta;
  legal: string;
  params: SupplyParams;
  /** фильтры адреса + умолчания /meta (`supplyQuery`) */
  q: SupplyQueryResult;
  supplies: ReturnType<typeof useWbSupplies>;
  onPatch: (p: Patch) => void;
  onOpenCard: (c: SupplyCardRef) => void;
}): React.ReactElement {
  if (q.error !== null) {
    return (
      <div role="alert" data-testid="bad-supply-filter" className="flex flex-wrap items-center gap-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
        <span>{q.error}</span>
        <button type="button" className="rounded border border-red-300 bg-background dark:border-red-800 px-2 py-0.5 text-foreground hover:bg-accent" onClick={() => onPatch(RESET_SUPPLY_FILTERS)}>
          Сбросить фильтры
        </button>
      </div>
    );
  }
  const query = q.query;
  return (
    <div className="space-y-2" data-testid="supplies-tab">
      <Filters meta={meta} params={params} status={query.status} since={query.since} onPatch={onPatch} />
      {supplies.isError ? (
        <ErrorBox error={supplies.error} what="поставки (/supplies)" />
      ) : !supplies.data ? (
        <Loading text="Загружаю поставки…" />
      ) : (
        <>
          <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground">
            <span data-testid="supplies-count">
              поставок: {supplies.data.count} из {supplies.data.count_all}
            </span>
            {supplies.isPlaceholderData && <span>обновляю…</span>}
            {supplies.data.excluded_no_supply_date > 0 && (
              <span className="text-amber-700 dark:text-amber-400" data-testid="excluded-no-date">
                {supplies.data.excluded_no_supply_date} поставок без даты скрыты фильтром «с даты»
              </span>
            )}
          </div>
          {supplies.data.supplies.length === 0 ? (
            <div className="rounded-md border bg-muted/30 px-4 py-6 text-sm" data-testid="supplies-empty">
              Ничего не найдено.{" "}
              <button type="button" className="underline" onClick={() => onPatch(RESET_SUPPLY_FILTERS)}>
                Сбросить фильтры
              </button>
            </div>
          ) : (
            <SuppliesTable data={supplies.data} showLegal={legal === WB_ALL} onOpenCard={onOpenCard} />
          )}
          <p className="text-[11px] text-muted-foreground">
            Путь: приёмка (СЦ / склад) → attr19 — подсказка кабинета, не склад поставки (красным — расходится с
            данными) → оценка склада поставки. «Сейчас» — места штук на день хранения кабинета (оценка).
            Флаг сверки у поставок на замороженном складе и в 1–2 шт. — серым «не значимо». «Запланирована» —
            ещё не принята (статус WB «Запланировано» / «Отгрузка разрешена»).
          </p>
        </>
      )}
    </div>
  );
}
